import { Dialog, DialogContent, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { SVForm } from '../../core';
import FormSubmissionButtons from '../../FormSubmissionButtons';
import {
  useCreateBankApplication,
  useUpdateBankApplication,
} from '@/hooks/requests/useBankApplications';
import {
  BankApplicationAccountType,
  BankApplicationFormValues,
} from '@/types/bankApplications';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import BankAccountApplicationBasicInfo from './BankAccountApplicationBasicInfo';
import BankAccountApplicationBeneficiaries from './BankAccountApplicationBeneficiaries';
import BankAccountApplicationExternalAccounts from './BankAccountApplicationExternalAccounts';
import BankAccountApplicationInitialDeposit from './BankAccountApplicationInitialDeposit';
import { useGetExternalAccountsList } from '@/hooks/requests/useExternalAccounts';
import { useEffect, useState } from 'react';
import BankAccountApplicationConfirm from './BankAccountApplicationConfirm';
import {
  useGetBusinessDetails,
  useGetBusinessesListForSelection,
} from '@/hooks/requests/useBusinesses';
import { useGetCustomerListForSelection } from '@/hooks/requests/useCustomers';
import { useGetTeammateListForSelection } from '@/hooks/requests/useTeammates';
import { StatusQueryParam } from '@/types/common';
import { useGetCompanyListForSelection } from '@/hooks/requests/useCompanies';
import { TeammateRole } from '@/types/teammates';
import BusinessForm from '@/components/businesses/BusinessForm';
import CustomersForm from '@/components/customers/CustomersForm';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { initialCustomerForm } from '@/constants/customers';
import { initialBusinessForm } from '@/constants/business';
import ExternalAccountForm from '@/components/externalAccounts/form/ExternalAccountForm';
import { initialExternalAccountForm } from '@/constants/externalAccounts';
import { ExternalBankAccountStatus } from '@/types/externalAccounts';

type BankAccountApplicationFormProps = {
  defaultValues: BankApplicationFormValues;
};

export default function BankAccountApplicationForm({
  defaultValues,
}: BankAccountApplicationFormProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [openOwnerModal, setOpenOwnerModal] = useState(false);
  const [openBusinessModal, setOpenBusinessModal] = useState(false);
  const [openExternalAccountModal, setOpenExternalAccountModal] =
    useState(false);

  const formMethods = useForm<BankApplicationFormValues>({
    defaultValues,
    mode: 'onBlur',
  });

  useEffect(() => {
    formMethods.reset();
  }, []);

  const [
    individualOwnerId,
    jointOwnerIds,
    businessId,
    advisorId,
    companyId,
    accountType,
    primaryOwner,
    applicationId,
  ] = formMethods.watch([
    'individualOwnerId',
    'jointOwnerIds',
    'businessId',
    'advisorId',
    'companyId',
    'accountType',
    'primaryOwner',
    'applicationId',
  ]);

  const hasOwnersSelected =
    individualOwnerId !== '' || jointOwnerIds.length >= 1;

  const {
    externalAccounts,
    externalAccountsForSelection,
    externalAccountsIsPending,
    refetchExternalAccounts,
  } = useGetExternalAccountsList(
    {
      consumer_ids: individualOwnerId ? [individualOwnerId] : jointOwnerIds,
      statuses: [
        ExternalBankAccountStatus.VERIFIED,
        ExternalBankAccountStatus.PENDING,
        ExternalBankAccountStatus.SUBMITTED,
        ExternalBankAccountStatus.AWAITING_BANK_ADDRESS,
        ExternalBankAccountStatus.AWAITING_BANK_NAME,
        ExternalBankAccountStatus.AWAITING_CODE,
        ExternalBankAccountStatus.AWAITING_E_SIGN,
      ],
    },
    hasOwnersSelected,
  );

  const { companiesList } = useGetCompanyListForSelection(
    {
      status: StatusQueryParam.ACTIVE,
    },
    true,
  );
  const { teammatesList } = useGetTeammateListForSelection(
    {
      status: StatusQueryParam.ACTIVE,
      company_id: companyId,
      roles: [TeammateRole.ADMIN, TeammateRole.ADVISOR],
    },
    !!companyId,
  );
  const { businessList } = useGetBusinessesListForSelection(
    {
      teammate_id: advisorId,
    },
    !!advisorId,
  );
  const { customerListByBoth } = useGetCustomerListForSelection(
    {
      company_id: companyId,
      teammate_ids: [advisorId],
    },
    !!advisorId,
  );

  const { businessDetails } = useGetBusinessDetails(businessId);
  useEffect(() => {
    if (businessDetails) {
      formMethods.setValue(
        'jointOwnerIds',
        businessDetails.members.map(member => member.consumer.uuid),
      );
      formMethods.setValue(
        'primaryOwner',
        businessDetails.controlPerson.uuid || '',
      );
    }
  }, [businessDetails]);

  const { createBankApplication, isCreateBankApplicationPending } =
    useCreateBankApplication();
  const { updateBankApplication, isUpdateBankApplicationPending } =
    useUpdateBankApplication();
  const isLoading =
    isCreateBankApplicationPending || isUpdateBankApplicationPending;

  function handleCustomerFormOnSucces(uuid: string) {
    setOpenOwnerModal(false);

    if (accountType === BankApplicationAccountType.INDIVIDUAL) {
      formMethods.setValue('individualOwnerId', uuid);
    } else {
      const newJointOwnerIds = [...jointOwnerIds];
      newJointOwnerIds.push(uuid);

      formMethods.setValue('jointOwnerIds', newJointOwnerIds);
    }
  }

  function handleBusinessFormOnSucces(uuid: string) {
    setOpenBusinessModal(false);
    formMethods.setValue('businessId', uuid);
  }

  function handleExternalAccountFormSuccess(uuid: string) {
    setOpenExternalAccountModal(false);
    formMethods.setValue('externalAccountDepositId', uuid);
    refetchExternalAccounts();
  }

  async function handleSubmit(data: BankApplicationFormValues) {
    if (data.beneficiaries.length) {
      const allocationPercentage = data.beneficiaries.reduce(
        (accumulator, member) => {
          return accumulator + Number(member.percentOfAllocation);
        },
        0,
      );
      if (allocationPercentage !== 100) {
        showToast({
          type: 'error',
          data: {
            content: 'Beneficiary allocation percentage must be equal to 100%',
          },
        });
        return;
      }
    }

    if (
      data.accountType === BankApplicationAccountType.JOINT &&
      data.jointOwnerIds.length < 2
    ) {
      showToast({
        type: 'error',
        data: {
          content:
            'You must have at least two owners associated to a joint bank account application',
        },
      });
      return;
    }

    if (
      data.accountType === BankApplicationAccountType.JOINT &&
      data.jointOwnerIds.length > 6
    ) {
      showToast({
        type: 'error',
        data: {
          content: 'You can not have more than 6 owners on a joint account',
        },
      });
      return;
    }

    try {
      if (!data.applicationId) {
        const response = await createBankApplication(data);
        await updateBankApplication({
          ...data,
          applicationId: response.uuid,
        });
        formMethods.setValue('applicationId', response.uuid);
        setShowConfirm(true);
        return;
      }

      await updateBankApplication(data);
      setShowConfirm(true);
    } catch {
      showToast({
        type: 'error',
        data: {
          content: MESSAGES.genericError,
        },
      });
    }
  }

  return (
    <>
      <SVForm methods={formMethods} onSubmit={handleSubmit}>
        <Dialog
          fullWidth
          open={showConfirm}
          onClose={() => setShowConfirm(false)}
          maxWidth="xl">
          <DialogContent>
            <BankAccountApplicationConfirm
              setShowConfirm={setShowConfirm}
              customerList={customerListByBoth}
              teammatesList={teammatesList}
              businessList={businessList}
              externalAccounts={externalAccounts}
            />
          </DialogContent>
        </Dialog>
        <Stack spacing={2}>
          <BankAccountApplicationBasicInfo
            customerList={customerListByBoth}
            companiesList={companiesList}
            businessList={businessList}
            teammatesList={teammatesList}
            setOpenBusinessModal={setOpenBusinessModal}
            setOpenOwnerModal={setOpenOwnerModal}
          />
          {hasOwnersSelected && (
            <>
              {accountType !== BankApplicationAccountType.BUSINESS && (
                <BankAccountApplicationBeneficiaries />
              )}
              <BankAccountApplicationExternalAccounts
                externalAccounts={externalAccounts}
                externalAccountsIsPending={externalAccountsIsPending}
                setOpenExternalAccountModal={setOpenExternalAccountModal}
                createBankApplication={createBankApplication}
              />
              <BankAccountApplicationInitialDeposit
                externalAccountsForSelection={externalAccountsForSelection}
              />
            </>
          )}
          <FormSubmissionButtons
            isLoading={isLoading}
            submitButtonLabel="Review"
          />
        </Stack>
      </SVForm>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openOwnerModal}
        onClose={() => setOpenOwnerModal(false)}>
        <DialogContent>
          <DefaultLayout header="Add Owner">
            <CustomersForm
              isEdit={false}
              onSuccess={handleCustomerFormOnSucces}
              onLeave={() => setOpenOwnerModal(false)}
              defaultValues={{
                ...initialCustomerForm,
                company: companyId,
                advisors: [advisorId],
              }}
            />
          </DefaultLayout>
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openBusinessModal}
        onClose={() => setOpenBusinessModal(false)}>
        <DialogContent>
          <DefaultLayout header="Add Business">
            <BusinessForm
              disableCompanyAndAdvisor
              onSuccess={handleBusinessFormOnSucces}
              onLeave={() => setOpenBusinessModal(false)}
              defaultValues={{
                ...initialBusinessForm,
                company: companyId,
                advisors: [advisorId],
              }}
            />
          </DefaultLayout>
        </DialogContent>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="lg"
        open={openExternalAccountModal}
        onClose={() => setOpenExternalAccountModal(false)}>
        <DialogContent>
          <DefaultLayout header="Add External Account">
            <ExternalAccountForm
              defaultValues={{
                ...initialExternalAccountForm,
                bankAccountApplicationId: applicationId,
                companyId: companyId,
                customerId: individualOwnerId
                  ? individualOwnerId
                  : jointOwnerIds.length === 1
                    ? jointOwnerIds[0]
                    : primaryOwner,
              }}
              showInactiveCustomers
              onLeave={() => setOpenExternalAccountModal(false)}
              onSuccess={handleExternalAccountFormSuccess}
            />
          </DefaultLayout>
        </DialogContent>
      </Dialog>
    </>
  );
}
